import React, { Suspense, useEffect, useState } from "react"
import mapboxgl from "mapbox-gl"
import { City } from "app/features/regional-map/regional-map.types"
import { ACCESS_TOKEN } from "hooks/use_map"
import { Combobox, ComboboxOptions, ComboboxOption, ComboboxInput } from "@headlessui/react"
import { MagnifyingGlassIcon } from "@radix-ui/react-icons"
import { useQuery } from "@blitzjs/rpc"
// import getLocationEnvelope from "app/municipalities/queries/getLocationEnvelope"
import getMunicipalities from "app/municipalities/queries/getMunicipalities"
import { useSetAtom } from "jotai"
import { hasUsedGeocoderAtom } from "app/state"
import { Municipality } from "db"
import { useTranslation } from "react-i18next"
import getGeocoderGeoid from "app/queries/get-geocoder-geoid"
import { getBbox } from "app/utils/scenario_utils"
import getBlockgroupGeometry from "app/blockgroups/queries/getBlockgroupGeometry"
import type { BBox } from "geojson"
export type MapboxGeoCoderResult = {
  place_name: string
  place_type: string
  center?: mapboxgl.LngLatLike
  bbox?: BBox
}
function GeoCoderResults(props: {
  mapboxGeocoderResults: MapboxGeoCoderResult[]
  municipalitySearchQuery: string
  cityId: string
}) {
  const { mapboxGeocoderResults, municipalitySearchQuery, cityId } = props

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [municipalities]: any = useQuery(
    getMunicipalities,
    { query: municipalitySearchQuery },
    { enabled: cityId === "national" && municipalitySearchQuery.length > 0 }
  )

  return (
    <ComboboxOptions className="bg-white">
      {cityId === "national" &&
        municipalities?.map((item: Municipality, i: number) => (
          <ComboboxOption value={{ ...item, optionType: "municipality" }} key={i}>
            <div className="text-bold cursor-pointer px-3 py-2 text-left text-sm text-brand-green-dark hover:bg-[#ECECEC]">
              {item.incorporated_place_name}, {item.state.toUpperCase()}
            </div>
          </ComboboxOption>
        ))}
      {mapboxGeocoderResults.map((item, i) => (
        <ComboboxOption value={{ ...item, optionType: "mapbox_geocoder_result" }} key={i}>
          {({ active }) => {
            return (
              <div
                className={`cursor-pointer px-3 py-2 text-left text-sm text-[#5A5A5A] ${active ? "bg-[#ECECEC]" : "hover:bg-[#ECECEC]"}`}
              >
                {item.place_name}
              </div>
            )
          }}
        </ComboboxOption>
      ))}
    </ComboboxOptions>
  )
}

export default function GeoCoder(props: { map: mapboxgl.Map | null; city: City }) {
  const { t } = useTranslation(["map"])
  const { map, city } = props
  const [query, setQuery] = useState<string>("")
  const [list, setList] = useState<MapboxGeoCoderResult[]>([])
  const [marker, setMarker] = useState<mapboxgl.Marker | null>(null)
  const setHasUsedGeocoder = useSetAtom(hasUsedGeocoderAtom)

  // const [envelope] = useQuery(
  //   getLocationEnvelope,
  //   { city: city.id.toUpperCase() },
  //   { enabled: city.id !== "national" },
  // )
  const isTwelveDigitNumber = /^\d{12}$/.test(query)
  const [blockgroup] = useQuery(getGeocoderGeoid, { id: query }, { enabled: isTwelveDigitNumber })
  const [feature] = useQuery(
    getBlockgroupGeometry,
    { id: blockgroup?.id as string },
    { enabled: !!blockgroup }
  )
  useEffect(() => {
    if (!feature) return
    const bbox = getBbox(feature)
    setList([
      {
        bbox,
        place_name: `${blockgroup?.id} ${blockgroup?.incorporated_place_name}, ${blockgroup?.state}`,
        place_type: "blockgroup",
      },
    ])
  }, [feature, setList, blockgroup])

  useEffect(() => {
    if (!map) return
    if (!query) {
      setList([])
      return
    }
    if (feature) return

    const mapCenter = map.getCenter()
    const centerPoint =
      city.id === "national" ? `${mapCenter.lng},${mapCenter.lat}` : city.centerpoint
    const params = {
      access_token: ACCESS_TOKEN,
      country: city.id === "toronto" ? "CA" : "US",
      proximity: centerPoint,
      types: `address,poi,locality,neighborhood,postcode${city.id !== "national" ? ",place" : ""}`,
      // bbox: city.id !== "national" && envelope ? envelope.join(",") : "",
    }
    const queryString = new URLSearchParams(params).toString()
    fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        query + `${city.id === "national" ? "" : " " + city.geocoderPostfix}`
      )}.json?${queryString}`
    )
      .then((res) => res.json())
      .then((res) => {
        setList(res.features)
      })
    // }, [setList, query, city, envelope])
  }, [setList, query, city, feature])

  return (
    <div className="divide-gray-300 text-sm">
      <Combobox
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value={null as any}
        onChange={(item) => {
          setHasUsedGeocoder(true)

          // remove marker if new item is selected
          if (marker) marker.remove()

          // add marker to map if item is a mapbox query and is an address or poi
          if (
            item &&
            item.optionType === "mapbox_geocoder_result" &&
            (item.place_type.includes("address") || item.place_type.includes("poi"))
          ) {
            const myMarker = new mapboxgl.Marker().setLngLat(item.center).addTo(map as mapboxgl.Map)

            setMarker(myMarker)
          }

          if (item) {
            if (item.bbox) {
              map?.fitBounds(item.bbox)
            } else {
              map?.flyTo({
                center: item.center,
                zoom: item.option_type === "mapbox_geocoder_result" ? 13 : 10,
              })
            }

            setQuery("")
            setList([])
          }
        }}
      >
        <div className="relative">
          <ComboboxInput
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
            }}
            placeholder={t("map:search_placeholder")}
            id="search-geocoder"
            aria-label="Search"
            className="text-md block w-full border-none bg-gray-100 px-4 py-3 text-black ring-0"
          />
          <MagnifyingGlassIcon className="pointer-events-none absolute right-3 top-3 h-6 w-6 text-gray-400" />
        </div>
        <Suspense fallback={null}>
          <GeoCoderResults
            mapboxGeocoderResults={list}
            municipalitySearchQuery={query}
            cityId={city.id}
          />
        </Suspense>
      </Combobox>
    </div>
  )
}
